import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { api } from '../../services/api';
import { PoolType } from '../../types/pool';

export function AdminCreatePool() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();

  // Form state
  const [name, setName] = useState('');
  const [type, setType] = useState<PoolType>(PoolType.CFP);
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [entryFee, setEntryFee] = useState('0');
  const [maxEntries, setMaxEntries] = useState('');
  const [deadline, setDeadline] = useState('');
  
  // Prize structure
  const [firstPlace, setFirstPlace] = useState('60');
  const [secondPlace, setSecondPlace] = useState('30');
  const [thirdPlace, setThirdPlace] = useState('10');

  // UI state
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pool type information
  const poolTypeInfo: Record<PoolType, { label: string; description: string }> = {
    [PoolType.CFP]: {
      label: 'College Football Playoff',
      description: '10 players (QB, 2 RB, 2 WR, TE, 2 FLEX, K, DEF) from 12 CFP teams'
    },
    [PoolType.NFL_PLAYOFF]: {
      label: 'NFL Playoff Fantasy',
      description: '12 players (QB, 2 RB, 2 WR, TE, 4 FLEX, K, DEF) from 14 NFL playoff teams'
    },
    [PoolType.NHL_PLAYOFF]: {
      label: 'NHL Playoff Fantasy',
      description: '12 players (2 C, 2 LW, 2 RW, 4 D, 2 G) from 16 NHL playoff teams'
    },
    [PoolType.NBA_PLAYOFF]: {
      label: 'NBA Playoff Fantasy',
      description: '12 players (4 Guards, 4 Forwards, 2 Centers, 2 Flex) from 16 NBA playoff teams'
    },
    [PoolType.FOUR_NATIONS]: {
      label: '4 Nations Face-Off',
      description: '8 players (4 F, 3 D, 1 G) with max 2 per country'
    },
    [PoolType.OLYMPIC_HOCKEY]: {
      label: 'Olympic Hockey',
      description: '10 players (5 F, 4 D, 1 G) with one player per country'
    },
    [PoolType.NCAA_BASKETBALL_PICK6]: {
      label: 'March Madness Pick 6',
      description: 'Select 6 teams from the tournament bracket'
    },
    [PoolType.NCAA_BASKETBALL_KNOCKOUT]: {
      label: 'March Madness Knockout',
      description: 'Daily picks with buy-back system'
    },
    [PoolType.FORMULA_1]: {
      label: 'Formula 1 Season',
      description: '2 drivers per race with usage limits'
    },
    [PoolType.WORLD_CUP_PICK6]: {
      label: 'World Cup Pick 6',
      description: 'Select 6 of the 48 World Cup nations; seed × round factor scoring'
    },
    [PoolType.WORLD_CUP_SURVIVOR]: {
      label: 'World Cup Survivor',
      description: 'Pick 6 group-stage advancers, then one team per knockout round (one-and-done)'
    },
    [PoolType.NFL_NO_REPEAT]: {
      label: 'NFL No-Repeat Challenge',
      description: 'Weekly 7-player offense lineup (QB, 2 RB, 2 WR, TE, FLEX) for Weeks 1-18; each player usable only once all season'
    },
  };

  const validateForm = (): string | null => {
    if (!name.trim()) return 'Pool name is required';
    if (!deadline) return 'Entry deadline is required';
    
    // Validate deadline is in the future
    const deadlineDate = new Date(deadline);
    if (deadlineDate <= new Date()) {
      return 'Deadline must be in the future';
    }

    // Validate prize structure
    const total = parseFloat(firstPlace) + parseFloat(secondPlace) + parseFloat(thirdPlace);
    if (Math.abs(total - 100) > 0.01) {
      return 'Prize structure must total 100%';
    }

    // Validate entry fee
    const fee = parseFloat(entryFee);
    if (isNaN(fee) || fee < 0) {
      return 'Entry fee must be a valid non-negative number';
    }

    // Validate max entries if provided
    if (maxEntries) {
      const max = parseInt(maxEntries);
      if (isNaN(max) || max < 1) {
        return 'Max entries must be a valid positive number';
      }
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setCreating(true);
      setError(null);
      const token = await getAccessTokenSilently();
      
      const pool = await api.createPool({
        name: name.trim(),
        type,
        description: description.trim() || undefined,
        isPublic,
        entryFee: parseFloat(entryFee),
        maxEntries: maxEntries ? parseInt(maxEntries) : undefined,
        deadline: new Date(deadline).toISOString(),
        prizeStructure: {
          first: parseFloat(firstPlace),
          second: parseFloat(secondPlace),
          third: parseFloat(thirdPlace),
        },
      }, token);

      // Navigate to import page for the new pool
      navigate(`/admin/pools/${pool.id}/import`);
    } catch (err) {
      console.error('Failed to create pool:', err);
      setError(err instanceof Error ? err.message : 'Failed to create pool');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/admin')}
          className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-4"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Dashboard
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Create New Pool</h1>
        <p className="text-gray-600 mt-2">Set up a new fantasy pool for your sport</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Basic Information</h2>
          
          {/* Pool Name */}
          <div className="mb-4">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Pool Name <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ color: '#111827' }}
              placeholder="e.g., 2025 NFL Playoff Challenge"
              maxLength={100}
              required
            />
          </div>

          {/* Pool Type */}
          <div className="mb-4">
            <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-2">
              Pool Type <span className="text-red-600">*</span>
            </label>
            <select
              id="type"
              value={type}
              onChange={(e) => setType(e.target.value as PoolType)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ color: '#111827' }}
              required
            >
              {Object.values(PoolType).map((poolType) => (
                <option key={poolType} value={poolType}>
                  {poolTypeInfo[poolType].label}
                </option>
              ))}
            </select>
            <p className="text-sm text-gray-500 mt-1">
              {poolTypeInfo[type].description}
            </p>
          </div>

          {/* Description */}
          <div className="mb-4">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description (Optional)
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ color: '#111827' }}
              placeholder="Describe your pool rules, special conditions, etc."
              rows={3}
              maxLength={500}
            />
          </div>

          {/* Public/Private */}
          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">
                Public Pool (visible to all users)
              </span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-6">
              {isPublic 
                ? 'Anyone can view and join this pool'
                : 'Only users with the link can join this pool'}
            </p>
          </div>
        </div>

        {/* Entry Settings */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Entry Settings</h2>
          
          {/* Entry Deadline */}
          <div className="mb-4">
            <label htmlFor="deadline" className="block text-sm font-medium text-gray-700 mb-2">
              Entry Deadline <span className="text-red-600">*</span>
            </label>
            <input
              type="datetime-local"
              id="deadline"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ color: '#111827' }}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Entries lock at this time (before first event starts)
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Entry Fee */}
            <div>
              <label htmlFor="entryFee" className="block text-sm font-medium text-gray-700 mb-2">
                Entry Fee ($)
              </label>
              <input
                type="number"
                id="entryFee"
                value={entryFee}
                onChange={(e) => setEntryFee(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ color: '#111827' }}
                min="0"
                step="0.01"
                placeholder="0.00"
              />
              <p className="text-xs text-gray-500 mt-1">
                Set to 0 for free pools
              </p>
            </div>

            {/* Max Entries */}
            <div>
              <label htmlFor="maxEntries" className="block text-sm font-medium text-gray-700 mb-2">
                Max Entries (Optional)
              </label>
              <input
                type="number"
                id="maxEntries"
                value={maxEntries}
                onChange={(e) => setMaxEntries(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ color: '#111827' }}
                min="1"
                placeholder="Unlimited"
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave empty for unlimited
              </p>
            </div>
          </div>
        </div>

        {/* Prize Structure */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Prize Structure</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* 1st Place */}
            <div>
              <label htmlFor="firstPlace" className="block text-sm font-medium text-gray-700 mb-2">
                1st Place (%)
              </label>
              <input
                type="number"
                id="firstPlace"
                value={firstPlace}
                onChange={(e) => setFirstPlace(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ color: '#111827' }}
                min="0"
                max="100"
                step="0.01"
                required
              />
            </div>

            {/* 2nd Place */}
            <div>
              <label htmlFor="secondPlace" className="block text-sm font-medium text-gray-700 mb-2">
                2nd Place (%)
              </label>
              <input
                type="number"
                id="secondPlace"
                value={secondPlace}
                onChange={(e) => setSecondPlace(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ color: '#111827' }}
                min="0"
                max="100"
                step="0.01"
                required
              />
            </div>

            {/* 3rd Place */}
            <div>
              <label htmlFor="thirdPlace" className="block text-sm font-medium text-gray-700 mb-2">
                3rd Place (%)
              </label>
              <input
                type="number"
                id="thirdPlace"
                value={thirdPlace}
                onChange={(e) => setThirdPlace(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ color: '#111827' }}
                min="0"
                max="100"
                step="0.01"
                required
              />
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-sm text-gray-700">
              <span className="font-medium">Total:</span>{' '}
              {(parseFloat(firstPlace || '0') + parseFloat(secondPlace || '0') + parseFloat(thirdPlace || '0')).toFixed(2)}%
              {Math.abs((parseFloat(firstPlace || '0') + parseFloat(secondPlace || '0') + parseFloat(thirdPlace || '0')) - 100) > 0.01 && (
                <span className="text-red-600 ml-2">(Must equal 100%)</span>
              )}
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
            <div className="flex items-start">
              <svg className="w-5 h-5 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="font-medium">Error creating pool</p>
                <p className="text-sm mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Submit Buttons */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin')}
            disabled={creating}
            className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={creating}
            className="px-6 py-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {creating && (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            {creating ? 'Creating Pool...' : 'Create Pool'}
          </button>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-blue-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div className="text-sm text-blue-800">
              <p className="font-medium">Next Steps</p>
              <p className="mt-1">
                After creating the pool, you'll be redirected to import teams, players, and schedule data.
              </p>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
